/* eslint-disable no-param-reassign */

// ============================
// const

// [HP稼働] 階層制限を解除
/** カテゴリーを含んだ最大階層数 */
// export const MaxTreeHierarchy = 4;

/** 新しいカテゴリーのノードの名前 */
export const NewCategoryNodeName = "新しいカテゴリー";
/** 新しいフォルダーのノードの名前 */
export const NewFolderNodeName = "新しいフォルダー";
/** その他フォルダーのラベルの値 */
export const OtherTreeNodeLabel = "(その他)";
/** その他フォルダーのノード名前 */
export const OtherTreeNodeName = "__OtherTreeNode__";

/** コンテキストメニューの実行モード */
export const ActionNameAdd = "add";
export const ActionNameRename = "rename";
export const ActionNameRemove = "remove";
export const ActionNameMove = "move";

// ============================
// type

/** APIから取得するデータの定義（ユーザー／ユーザーグループごとに取得） */
export interface StructureApiType {
  path: string;
  name: string;
  count: number;
}

/** StructureApiTypeに画面操作用の項目を追加した定義 */
export interface StructureType extends StructureApiType {
  // childrenはTreeView描画時のToNest処理で再構築されるので
  // モデル操作では参照しない
  children: StructureType[];
  isDisplaySelf: boolean;
  isDisplaySelfOnFilter: boolean;
}

/** ユーザー／ユーザーグループごとの仮想フォルダ―構成の定義 */
export type TreeDataType = {
  virtual_folder_config_id: number;
  created_at: string;
  structure: StructureType[];
  expandedNode: string[];
  isDisplayAll: boolean;
};

// ============================
// private function

/** イテレート処理汎用関数 */
const Range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_v, k) => k + start);

/** パスの並び替え比較関数 */
const sortPathAsc = (flat1: StructureType, flat2: StructureType) => {
  if (flat1.path.toLowerCase() < flat2.path.toLowerCase()) return -1;
  if (flat1.path.toLowerCase() > flat2.path.toLowerCase()) return 1;
  return 0;
};

/** ネストされたjsonを１次元の配列にする */
const FlatChildren = (node: StructureType, result: StructureType[]) => {
  const hasChildren = node.children !== null && node.children.length > 0;
  if (hasChildren) {
    node.children.map(child => FlatChildren(child, result));
  }
  result.push(node);
  return result;
};

/** １次元の配列から指定したノードの子ノードを取得 */
const GetChildren = (flat: StructureType[], parent: StructureType) => {
  const regxChilren = RegExp(`^${parent.path}/[^/]+$`);
  const childrenFilter = (tree: StructureType) => {
    return regxChilren.test(tree.path);
  };
  const children: StructureType[] = flat.filter(childrenFilter);
  const hasChildren = children !== null && children.length > 0;
  parent.children = children.sort(sortPathAsc);
  if (hasChildren) {
    children.map(child => GetChildren(flat, child));
  }
  // console.log(parent.path);
  return parent;
};

// ============================
// public function

/** ノードのパスから名前を取得 */
export const PathToNodeName = (path: string) => {
  const prefixIndex = path.lastIndexOf("/");
  if (prefixIndex < 0) {
    // 自分がルートの場合
    return path;
  }
  // 自分が子の場合
  return path.slice(prefixIndex + 1);
};

/** ノードのパスから親ノードのパスを取得 */
export const PathToParentPath = (path: string) => {
  const prefixIndex = path.lastIndexOf("/");
  if (prefixIndex < 0) {
    // 自分がルートの場合
    return "";
  }
  // 自分が子の場合
  return path.slice(0, prefixIndex);
};

/** ノードのパスから祖父ノードのパスを取得 */
export const PathToGrandParentPath = (path: string) => {
  const pathArray = path.split("/");
  if (pathArray.length < 3) {
    // 祖父ノードがない場合
    return undefined;
  }
  const result = pathArray.slice(0, pathArray.length - 2).join("/");
  return result;
};

/** ノードのパスから直系の祖先ノードのパスを取得 */
export const PathToAncestorPath = (path: string) => {
  const splitedPath = path.split("/");
  const splitCount = splitedPath.length;
  const ancestorPath: string[] = Range(0, splitCount - 2).map(index => {
    return splitedPath.slice(0, index + 1).join("/");
  });
  return ancestorPath;
};

/** ノードのパスから親ノードを取得 */
export const PathToGrandParentNode = (
  flat: StructureType[] | undefined,
  path: string
) => {
  if (!flat) return {} as StructureType;
  const grandParentPath = PathToGrandParentPath(path);
  const finded = flat.find(tree => tree.path === grandParentPath);
  return finded || undefined;
};

/** ノードのパスから祖父ノードをリストで取得 */
export const PathToGrandParentNodeOnList = (
  flat: StructureType[] | undefined,
  path: string
) => {
  const newFlat: StructureType[] = [];
  const grandParentNode = PathToGrandParentNode(flat, path);
  if (grandParentNode) {
    newFlat.push(grandParentNode);
  }
  return newFlat;
};

/** ノードのパスから兄弟ノード（自分以外）を取得 */
export const PathToBrothersNode = (
  flat: StructureType[] | undefined,
  path: string
) => {
  const newFlat: StructureType[] = [];
  if (!flat) return newFlat;
  const parentPath = PathToParentPath(path);
  flat.forEach(tree => {
    const isMatchBrothers = RegExp(`^${parentPath}/[^/]+$`).test(tree.path);
    if (isMatchBrothers && tree.path !== path) {
      newFlat.push(tree);
    }
  });
  return newFlat;
};

/** ノードがカテゴリーかの判定 */
export const IsCategoryNode = (path: string) => {
  return path.split("/").length === 1;
};

// [HP稼働] 階層制限を解除
// /** ノードに新しいフォルダ―を追加できるかの判定（階層の制限あり） */
// export const CanAddTreeNode = (path: string) => {
//   const pathArray = path.split("/");
//   return pathArray.length < MaxTreeHierarchy;
// };

// [HP稼働] 階層制限を解除
// /** 階層の制限を超えているかの判定（フォルダ―の移動後のチェックに使用） */
// export const OverMaxTreeHierarchy = (flat: StructureType[]) => {
//   const index = flat.findIndex(
//     node => node.path.split("/").length > MaxTreeHierarchy
//   );
//   return index > -1;
// };

/** ノードにフォルダ―の移動対象があるかの判定 */
export const ExistsMoveTarget = (
  flat: StructureType[] | undefined,
  path: string
) => {
  const grandParentNodeCount = PathToGrandParentNodeOnList(flat, path).length;
  const brotherNodeCount = PathToBrothersNode(flat, path).length;
  return grandParentNodeCount + brotherNodeCount > 0;
};

/** ネストされたjsonを１次元の配列にする */
export const ToFlat = (nest: StructureType[]) => {
  const result: StructureType[] = [];
  if (nest.length > 0) {
    nest.map(root => FlatChildren(root, result));
  }
  return result;
};

/** １次元の配列をネストした構成に変換 */
export const ToNest = (flat: StructureType[]) => {
  const regxRoots = RegExp("^[^/]+$");
  const rootsFilter = (tree: StructureType) => {
    return regxRoots.test(tree.path);
  };
  const roots: StructureType[] = flat.filter(rootsFilter);
  const hasRoots = roots !== null && roots.length > 0;
  if (hasRoots) {
    roots.map(root => GetChildren(flat, root));
  }
  return roots.sort(sortPathAsc);
};

/** ノードの表示切替 */
export const ToggleDisplaySelf = (
  flat: StructureType[],
  path: string,
  checked: boolean
) => {
  // 子孫のノードは全て自分と同じ設定
  // 直系の祖先のノードは、自分がtrueの場合、全てtrueにする
  const newFlat: StructureType[] = [];
  const ancestorPath: string[] = PathToAncestorPath(path);
  flat.forEach(tree => {
    const isMatch = RegExp(`^${path}$`).test(tree.path);
    const isMatchProgeny = RegExp(`^${path}/`).test(tree.path);
    if (isMatch || isMatchProgeny) {
      newFlat.push({ ...tree, isDisplaySelf: checked });
    } else if (checked && ancestorPath.includes(tree.path)) {
      newFlat.push({ ...tree, isDisplaySelf: checked });
    } else {
      newFlat.push({ ...tree });
    }
  });
  return newFlat;
};

/** ノードが存在するかの判定 */
export const ExistsNode = (flat: StructureType[], path: string) => {
  const find: StructureType | undefined = flat.find(node => node.path === path);
  if (find) {
    return true;
  }
  return false;
};

/** ノードの追加(新しいカテゴリ―／新しいフォルダ―アクション) */
export const AddNode = (
  flat: StructureType[],
  parentPath: string,
  name: string
) => {
  const newPath = parentPath === "" ? name : `${parentPath}/${name}`;
  const node: StructureType = {
    path: newPath,
    name,
    isDisplaySelf: true,
    isDisplaySelfOnFilter: true,
    children: [],
    count: -1,
  };
  flat.push(node);
  const newFlat = ToggleDisplaySelf(flat, newPath, true);
  return newFlat;
};

/** ノードの名前変更（名前の変更アクション） */
export const RenameNode = (
  flat: StructureType[],
  path: string,
  newName: string
) => {
  const parentPath = PathToParentPath(path);
  const newPath = parentPath === "" ? newName : `${parentPath}/${newName}`;
  const newFlat: StructureType[] = [];
  flat.forEach(tree => {
    const isMatchSelf = RegExp(`^${path}$`).test(tree.path);
    const isMatchProgeny = RegExp(`^${path}/`).test(tree.path);
    if (isMatchSelf) {
      newFlat.push({
        ...tree,
        name: newName,
        path: tree.path.replace(RegExp(`^${path}`), newPath),
      });
    } else if (isMatchProgeny) {
      newFlat.push({
        ...tree,
        path: tree.path.replace(RegExp(`^${path}`), newPath),
      });
    } else {
      newFlat.push({ ...tree });
    }
  });
  return newFlat;
};

/** ノードの移動（フォルダ―の移動アクション） */
export const MoveNode = (
  flat: StructureType[],
  path: string,
  targetPath: string
) => {
  const parentPath = PathToParentPath(path);
  const newFlat: StructureType[] = [];
  flat.forEach(tree => {
    const isMatchSelf = RegExp(`^${path}$`).test(tree.path);
    const isMatchProgeny = RegExp(`^${path}/`).test(tree.path);
    if (isMatchSelf || isMatchProgeny) {
      newFlat.push({
        ...tree,
        path: tree.path.replace(RegExp(`^${parentPath}`), targetPath),
      });
    } else {
      newFlat.push({ ...tree });
    }
  });
  return newFlat;
};

/** ノードの再帰的な削除（フォルダ―の削除アクション） */
export const RemoveNode = (flat: StructureType[], path: string) => {
  const regexRemoveSelf = RegExp(`^${path}$`);
  const regexRemoveProgeny = RegExp(`^${path}/`);
  const leaveNodeFilter = (node: StructureType) => {
    return (
      !regexRemoveSelf.test(node.path) && !regexRemoveProgeny.test(node.path)
    );
  };
  const filtered: StructureType[] = flat.filter(leaveNodeFilter);
  return filtered;
};

/** 展開ノードの名前を変更する */
export const RenameExpanded = (
  expanded: string[],
  path: string,
  newName: string
) => {
  const parentPath = PathToParentPath(path);
  const newPath = parentPath === "" ? newName : `${parentPath}/${newName}`;
  const renamedExpanded = expanded.map(exPath => {
    const isMatchSelf = RegExp(`^${path}$`).test(exPath);
    const isMatchProgeny = RegExp(`^${path}/`).test(exPath);
    if (isMatchSelf || isMatchProgeny) {
      return exPath.replace(RegExp(`^${path}`), newPath);
    }
    return exPath;
  });
  return renamedExpanded;
};

/** 展開ノードの名前を変更する（名前の変更／フォルダ―の移動アクション） */
export const MoveExpanded = (
  expanded: string[],
  path: string,
  targetPath: string
) => {
  const parentPath = PathToParentPath(path);
  const renamedExpanded = expanded.map(exPath => {
    const isMatchSelf = RegExp(`^${path}$`).test(exPath);
    const isMatchProgeny = RegExp(`^${path}/`).test(exPath);
    if (isMatchSelf || isMatchProgeny) {
      return exPath.replace(RegExp(`^${parentPath}`), targetPath);
    }
    return exPath;
  });
  return renamedExpanded;
};

/** ノードのリストにないパスを展開ノードのリストから削除 */
export const RemoveExpanded = (expanded: string[], flat: StructureType[]) => {
  const paths = flat.map(tree => tree.path);
  const removedExpanded: string[] = expanded.filter((path: string) => {
    return paths.includes(path);
  });
  return removedExpanded;
};

/** APIから取得したデータの初期化 */
export const InitializeTreeData = (configId: number) => {
  return {
    virtual_folder_config_id: configId,
    structure: [],
    expandedNode: [],
    created_at: "1900-01-01T00:00",
    isDisplayAll: false,
  } as TreeDataType;
};

/** APIから取得したデータとローカル設定のマージ */
export const MergeTreeData = (
  apiData: TreeDataType,
  mergeData: TreeDataType | undefined
) => {
  if (!mergeData) return apiData;

  // eslint-disable-next-line array-callback-return
  apiData.structure.map(structure => {
    // childrenは描画時のToNestでセットするので初期化のみ
    structure.children = [];

    // ノードの表示／非表示設定をマージ
    const samePathStructure = mergeData.structure.find(
      str => structure.path === str.path
    );
    if (samePathStructure) {
      structure.isDisplaySelf = samePathStructure.isDisplaySelf;
    } else {
      structure.isDisplaySelf = true;
    }

    // フィルターでの表示／非表示設定の初期値を設定
    structure.isDisplaySelfOnFilter = true;
  });
  // 不要な展開ノードは削除してマージ
  apiData.expandedNode = RemoveExpanded(
    mergeData.expandedNode,
    apiData.structure
  );
  // 全ノード表示するかのフラグをマージ
  apiData.isDisplayAll = mergeData.isDisplayAll;
  return apiData;
};

/** その他フォルダ―の件数を計算 */
export const CountOtherTreeNode = (parent: StructureType) => {
  const reducer = (accumulator: number, currentValue: number) => {
    return accumulator + currentValue;
  };
  // その他フォルダの件数は、親か兄弟の件数に-1があれば、計算しない
  if (
    parent.count < 0 ||
    parent.children.findIndex(el => {
      return el.count < 0;
    }) >= 0
  ) {
    return -1;
  }
  // 親の件数から個の件数の合計を引いた数をその他フォルダの件数とする
  const countSum = parent.count;
  const countChildren = parent.children.map(el => el.count).reduce(reducer);
  return countSum - countChildren;
};

/** その他フォルダ―を選択した場合の重要語の除外リスト作成 */
export const GetExcludeNodeNames = (
  nodeId: string,
  parent: StructureType | undefined
) => {
  // 選択したノードがその他フォルダーの場合は、除外リスト返却
  if (nodeId.indexOf(OtherTreeNodeName) > 0) {
    if (!parent) {
      return [];
    }
    if (parent.children && parent.children.length > 0) {
      return parent.children.map((child: StructureType) => {
        return child.name;
      });
    }
  }
  return [];
};

/** 指定したノードとその配下かの判定（レンダーの判断に使用） */
export const IsSelfOrProgeny = (parentPath: string, path: string) => {
  const ancestorPath = PathToAncestorPath(parentPath);
  const isMatchSelf = RegExp(`^${parentPath}$`).test(path);
  const isMatchProgeny = RegExp(`^${parentPath}/`).test(path);
  const isMatchAncestor = ancestorPath.includes(path);
  return isMatchAncestor || isMatchSelf || isMatchProgeny;
};
